import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { FaUpload, FaList, FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import FileModal from "../../components/FileModal";
import FileProcessingStatusModal from "../../components/FileProcessingStatusModal";
import { FileEntity } from "@quantizr-engineering/common/types/file";
import { ContractType, Show } from "@quantizr-engineering/common/types/show";
import ShowOfferFileModal from "../../components/ShowOfferFileModal";
import documentGradient from "../../assets/document-gradient.svg";
import addCircle from "../../assets/add-circle.svg";
import { toast } from "sonner";
import { useTeamService } from "../../api/teamService";

export function parseJwt(token: string) {
  var base64Url = token.split(".")[1];
  var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  var jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
}

const Dashboard: React.FC = () => {
  const [showFileModal, setShowFileModal] = useState(false);
  const [showFileProcessingStatusModal, setShowFileProcessingStatusModal] =
    useState(false);
  const [processingFiles, setProcessingFiles] = useState<Partial<FileEntity>[]>(
    []
  );
  const { createNewTeam } = useTeamService();

  const token = localStorage.getItem("authToken");
  const user = parseJwt(token || "");

  const navigate = useNavigate();

  const actionItems = [
    {
      title: "Analyze Show Offer",
      icon: documentGradient,
      alt: "Document",
      onClick: () => setShowFileModal(true),
    },
    {
      title: "Add New Team",
      icon: addCircle,
      alt: "Add Team",
      onClick: () => {
        toast(
          <div className="flex flex-col gap-2">
            <span>New Team Name</span>
            <input
              type="text"
              className="px-2 py-1 border rounded text-black"
              placeholder="Team Name"
              id="dashboard-new-team"
              autoFocus
            />
          </div>,
          {
            duration: 30000,
            action: {
              label: "Submit",
              onClick: async () => {
                const teamNameInput = document.getElementById(
                  "dashboard-new-team"
                ) as HTMLInputElement;
                const teamName = teamNameInput?.value;
                if (teamName) {
                  toast.info(`Creating new team: ${teamName}...`);
                  await createNewTeam(teamName);
                } else {
                  toast.error("Please enter a valid team name");
                }
              },
            },
            cancel: {
              label: "Cancel",
              onClick: () => {},
            },
          }
        );
      },
    },
  ];

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow-md p-6 mb-8 max-w-[300px]">
        <div className="text-sm font-semibold text-gray-800 mb-2 text-start">
          Hi {user.firstName} - you are working in
        </div>
        <div className="text-3xl font-bold bg-gradient-to-tr from-custom-teal to-custom-dark-purple via-custom-purple bg-clip-text text-transparent text-center">
          {user.teamName}
        </div>
      </div>
      <h2 className="text-2xl font-semibold mt-8 mb-4">Quick Actions</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-[500px]">
        {actionItems.map((item, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 aspect-square"
            onClick={item.onClick}
          >
            <div className="text-center p-4 flex items-center justify-center h-full">
              <div className="flex flex-col items-center">
                <div className="text-4xl text-blue-500 mb-2">
                  <img src={item.icon} alt={item.alt} className="w-12 h-12" />
                </div>
                <h3 className="text-xl font-semibold">{item.title}</h3>
              </div>
            </div>
          </div>
        ))}
      </div>
      {showFileModal && (
        <ShowOfferFileModal
          onClose={(show: Show | null) => {
            if (!show) {
              setShowFileModal(false);
              return;
            }
            if (!!show) {
              navigate(`/runs/${show.runID}/shows/${show.id}`);
            }
            setShowFileModal(false);
          }}
        />
      )}
      {showFileProcessingStatusModal && (
        <FileProcessingStatusModal
          files={processingFiles}
          onClose={() => {
            setShowFileProcessingStatusModal(false);
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
